import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { coerce } from 'semver';
import { isMobile, isIOS } from 'react-device-detect';
import { useHistory, useLocation } from 'react-router-dom';
import PWAPrompt from 'react-ios-pwa-prompt';
import { useTranslation } from 'react-i18next';
import { addDays, isAfter, parseISO } from 'date-fns';
import PullToRefresh from 'pulltorefreshjs';
import _ from 'lodash';

import './App.scss';
import AppContext from './App.context';
import AppModeContext from './containers/AppMode/AppMode.context';
import { RootState } from './configs/store';
import useMobileOrientation from './hooks/useMobileOrientation';
import useIsMobile from './hooks/useIsMobile';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Toast from './components/Toast/Toast';
import Loading from './components/Loading/Loading';
import Modal from './components/Modal/Modal';
import ContentModal from './components/ContentModal/ContentModal';
import WindowScrollHandler from './components/WindowScrollHandler/WindowScrollHandler';
import SiteRouting from './containers/AppRouting/SiteRouting';
import RetailRouting from './containers/AppRouting/RetailRouting';
import TradingRouting from './containers/AppRouting/TradingRouting';
import NewWindow from 'react-new-window';
import ReadOnlyBetslip from './components/SecondScreen/ReadOnlyBetslip/ReadOnlyBetslip';
import SecondScreenFrame from './components/SecondScreen/SecondScreenFrame';
import CustomerLogin from './components/SecondScreen/CustomerLogin/CustomerLogin';
import Ticket from './components/SecondScreen/Ticket/Ticket';
import CancelTicket from './pages/RetailFeatures/CancelTicket/CancelTicket';

const TILL_SCREEN_WIDTH = 1000;
const TILL_SCREEN_WIDTH_HD = 1400;

function App() {
  const modals = useSelector((state: RootState) => state.content.modals);
  // const { authenticated } = useSelector((state: RootState) => state.auth);
  const { isPortrait, isLandscape } = useMobileOrientation();
  const mobile = useIsMobile();
  const { RETAIL_MODE, TRADING_MODE } = useContext(AppModeContext);
  const showSecondScreen = useSelector((state: RootState) => state.content.showSecondScreen);
  const secondScreenTicketCode = useSelector((state: RootState) => state.peripherals.secondScreenTicketCode);

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('common');
  const prevValueRef = useRef(null);
  const hiddenInput = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  const simulateClick = (e) => {
    if (e.key === 'Enter') {
      const submitElements:any = document.getElementsByClassName('modal-submit');
      if (submitElements.length > 0) {
        submitElements[0].click();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', simulateClick);

    return () => {
      document.removeEventListener('keydown', simulateClick);
    };
  }, []);

  const input = useRef(null);
  // const wallets = useRef(null);

  const context = {
    isLandscape,
    isPortrait
  };

  if (mobile) {
    document.body.classList.add('mobile');
    document.body.classList.remove('mobile-tablet');
  } else if (isMobile) {
    document.body.classList.add('mobile-tablet');
    document.body.classList.remove('mobile');
  } else {
    document.body.classList.remove('mobile');
    document.body.classList.remove('mobile-tablet');
  }

  if (isIOS) {
    document.body.classList.add('ios-device');
  }

  const addAxiosInterceptors = () => {
    // Add a request interceptor
    axios.interceptors.request.use(config => {
      // Do something before request is sent
      if (!config.url.includes(process.env.REACT_APP_TILL_AWAIT)){
        dispatch.content.enableLoading();
      }

      return config;
    }, error => {
      // Do something with request error
      dispatch.content.disableLoading();

      return Promise.reject(error);
    });

    // Add a response interceptor
    axios.interceptors.response.use(response => {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      dispatch.content.disableLoading();

      const sVersion = JSON.parse(localStorage.getItem('sVersion'));
      if (sVersion) {
        sVersion.n = response.headers['x-api-version'];
      }

      localStorage.setItem('sVersion', JSON.stringify(sVersion));

      return response;
    }, error => {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      dispatch.content.disableLoading();

      // Token is invalid. Logout the user.
      if (error.response.status === 400 && error.response.data.code === 'INVALID_HEADER') {
        history.push(`${ location.pathname }#logout`);
        dispatch.auth.logout();
      }

      return Promise.reject(error);
    });
  };

  const onPWARemindLater = () => {
    const date = addDays(new Date(), 7);
    localStorage.setItem('PWAStatus', date.toISOString());
  };

  const onPWAClose = () => {
    const promptData = JSON.parse(localStorage.getItem('iosPwaPrompt'));

    localStorage.setItem(
      'iosPwaPrompt',
      JSON.stringify({
        ...promptData,
        visits: 1001
      })
    );
  };

  // useEffect(() => {
  //   if (authenticated) {
  //     // Get wallets every minutes
  //     dispatch.auth.getWallets();
  //     wallets.current = setInterval(dispatch.auth.getWallets, 60 * 1000);
  //   }


  //   return () => {
  //     if (wallets.current) {
  //       clearInterval(wallets.current);
  //     }
  //   };
  // }, [authenticated, history]);

  useEffect(() => {
    dispatch.content.getSettings({ companyId: process.env.REACT_APP_COMPANY_ID, shopId: process.env.REACT_APP_SHOP_ID });
    if (RETAIL_MODE) {
      document.documentElement.classList.add('dark-theme', 'till');
    }

    const metaVersion = document.head.querySelector('[name~=sversion][content]');
    if (process.env.REACT_APP_GIT_TAG || metaVersion.getAttribute('content')) {
      localStorage.setItem('sVersion', JSON.stringify({ r: coerce(process.env.REACT_APP_GIT_TAG || metaVersion.getAttribute('content')).version }));
    }

    if (localStorage.getItem('token')) {
      dispatch.auth.getCustomer();
    }

    if (localStorage.getItem('till')) {
      dispatch.auth.updateTillUser();
    }

    if (localStorage.getItem('tradingToken')) {
      dispatch.auth.updateTradingUser();
    }

    addAxiosInterceptors();
  }, []);

  useEffect(() => {
    if (!prevValueRef.current) {
      // do componentDidMount logic
      prevValueRef.current = location.pathname;
    } else {
      // do componentDidUpdate logic
      prevValueRef.current = location.pathname;
      if (!prevValueRef.current.includes(location.pathname)) {
        dispatch.events.resetSearchResults();
      }
    }

    const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches;

    isPWAInstalled && isIOS && PullToRefresh.init({
      mainElement: 'body',
      onRefresh() {
        window.location.reload();
      }
    });
  });

  const handleResize = () => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight
    });
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
    if (RETAIL_MODE && dimensions.width >= TILL_SCREEN_WIDTH && dimensions.width < TILL_SCREEN_WIDTH_HD) {
      dispatch.content.changeSidebar({ type: 'leftSideBar', collapsed: true });
    }
  }, []);

  const checkIfElementIsInput = () => {
    const inputs = ['input', 'textarea'];

    if (document.activeElement && inputs.indexOf(document.activeElement.tagName.toLowerCase()) !== -1) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    //LEAVE THIS COMMENTED

    // if (RETAIL_MODE){
    //   let code = '';
    //   let reading = false;

    //   document.body.addEventListener('keypress', (e) => {
    //     if ((e.keyCode>=48 && e.keyCode<=57) || e.keyCode === 13){

    //       if (!checkIfElementIsInput()){
    //         hiddenInput.current.focus();
    //       } else {
    //         if (code.length >= 1){
    //           e.preventDefault();
    //         }
    //         if (e.key !== 'Enter'){
    //           code += e.key;
    //         }
    //       }

    //       if (!reading) {
    //         reading = true;
    //         setTimeout(() => {
    //           if (code.length > 2){
    //             const activeElement = (document.activeElement as HTMLInputElement);
    //             dispatch.peripherals.getBarcode(code);
    //             activeElement.value = '';
    //           }
    //           code = '';
    //           reading = false;
    //         }, 50);
    //       }
    //     }
    //   }
    //   );
    // }
    if (RETAIL_MODE){
      const ean = { time: 0, chars: [], timeout: 0, input: '' };
      if (!checkIfElementIsInput()){
        hiddenInput.current.focus();
      }

      window.addEventListener('keypress', (e) => {
        var now = new Date();
        var timeDiff = +now - +ean.time;
        var which = e.keyCode;
        ean.input = ean.input + e.key;
        input.current = document.activeElement;

        if (!checkIfElementIsInput()){
          hiddenInput.current.focus();
        }

        if (timeDiff > 50) {
          ean.chars = [];
        } else {
          e.preventDefault();
        }

        if (which >= 48 && which <= 57) {
          window.clearTimeout(ean.timeout);
          ean.chars.push(e.key);
          if (ean.chars.length >= 11) {
            e.preventDefault();
            const barcode = ean.chars.join('');
            dispatch.lotto.changeValues({ key: input.current.name, value: '' });
            if (ean.chars.length <= 12) {
              ean.timeout = window.setTimeout(() => {
                dispatch.lotto.changeValues({ key: input.current.name, value: '' });
                dispatch.peripherals.getBarcode(barcode);
              }, 50);
            } else {
              ean.timeout = window.setTimeout(() => {
                dispatch.lotto.changeValues({ key: input.current.name, value: '' });
                dispatch.peripherals.getBarcode(barcode);
              }, 50);
            }
          }
        } else if (ean.chars.length >= 11) {
          e.preventDefault();
        }
        ean.time = +now;
      });
    }
  }, [input.current, history.location.key]);

  const handleGetTicket = (ticket) => {
    dispatch.peripherals.changeTicketCode(ticket.code);
    dispatch.content.changeShowSecondScreen('ticket');
  };


  const PWADate = localStorage.getItem('PWAStatus');
  const isValidDate = _.isNull(PWADate) ? true : isAfter(new Date(), parseISO(PWADate));

  return (
    <AppContext.Provider value={ context }>
      { RETAIL_MODE && <input style={ { position: 'absolute', top:'-300px' } } ref={ hiddenInput } /> }
      <WindowScrollHandler />

      <ErrorBoundary>
        <div className="app-container">
          <Toast />
          <Loading />
          <Modal />
          { _.map(modals, (modal, i) => <ContentModal modal={ modal } key={ i } />) }

          { !RETAIL_MODE && !TRADING_MODE && <SiteRouting /> }
          { RETAIL_MODE && <RetailRouting /> }
          { TRADING_MODE && <TradingRouting /> }

          {
            isMobile && !RETAIL_MODE && !TRADING_MODE && isValidDate &&
              <PWAPrompt
                timesToShow={ 1000 }
                permanentlyHideOnDismiss={ false }
                copyTitle={ t('pwaPrompt.title') }
                copyBody={ t('pwaPrompt.body') }
                copyShareButtonLabel={ t('pwaPrompt.share') }
                copyAddHomeButtonLabel={ t('pwaPrompt.addHome') }
                copyClosePrompt={
                  <>
                    <span className="app-container__pwa-button" onClick={ onPWARemindLater }>{ t('pwaPrompt.remindLater') }</span>
                    <span className="app-container__pwa-button" onClick={ onPWAClose }>{ t('pwaPrompt.close') }</span>
                  </>
                }
              />
          }
          {
            showSecondScreen && RETAIL_MODE && <NewWindow
              features={
                {
                  width: 2048,
                  height: 768
                }
              }
              onUnload={ () => dispatch.content.changeShowSecondScreen('') }
            >
              <SecondScreenFrame>
                { showSecondScreen === 'betslip' && <ReadOnlyBetslip /> }
                { showSecondScreen === 'customerLogin' && <CustomerLogin /> }
                {
                  showSecondScreen === 'ticket' && secondScreenTicketCode &&
                    <Ticket />
                }
                {
                  showSecondScreen === 'showTicket' && !secondScreenTicketCode &&
                 <CancelTicket
                   isCancel={ false }
                   updateIsCancellable={ () => {} }
                   updateIsPayotable={ () => {} }
                   setDetails={ handleGetTicket }
                   isPayout={ false }
                 />
                }
              </SecondScreenFrame>
            </NewWindow>
          }
        </div>
      </ErrorBoundary>
    </AppContext.Provider>
  );

}

export default App;
